import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	inject,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';
import { TenantAccount } from '../../../../../services/model/account.model';
import {
	Organization,
	OrganizationService,
	OrganizationTree
} from '../../../../../services/organization.service';
import { Category } from '../../../../../services/model/application-category.model';
import {
	distinctUntilChanged,
	finalize,
	forkJoin,
	merge,
	Observable,
	of,
	Subject,
	switchMap,
	tap,
	map,
	debounceTime
} from 'rxjs';
import { CurrentTenantService } from '../../../../../services/front/current-tenant.service';
import { TenantAccountService } from '../../../../../services/back/tenant-account.service';
import { ApplicationCategoryService } from '../../../../../services/back/application-category.service';
import { NewApplicationService } from '../../../../../services/back/new-application.service';
import { CatalogTag } from '../../../../../services/model/catalog-tag.model';
import {
	ApplicationStatus,
	ApplicationTableFilterForm,
	HostingType
} from '../../../../../services/model/new-application.model';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import { CriticalityLevel } from '../../../../../services/tenant.service';
import { takeUntil } from 'rxjs/operators';
import {DesignSystemModule} from "../../../../design-system/design-system.module";
import {DisplayAppStatusComponent} from "../../../../design-system/display-app-status/display-app-status.component";
import {CommonModule} from "@angular/common";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {DropdownTriggerDirective} from "../../../../design-system/dropdown/dropdown-trigger.directive";
import {animate, state, style, transition, trigger} from "@angular/animations";

@Component({
	selector: 'app-application-list-filter',
	imports: [
		DesignSystemModule,
		DisplayAppStatusComponent,
		FormsModule,
		TranslateModule,
		CommonModule,
		ReactiveFormsModule,
		DropdownTriggerDirective
	],
	templateUrl: './application-list-filter.component.html',
	styleUrl: './application-list-filter.component.scss',
	animations: [
		trigger('expandCollapse', [
			state('collapsed', style({
				height: '0',
				overflow: 'hidden',
				opacity: 0,
			})),
			state('expanded', style({
				height: '*',
				overflow: 'hidden',
				opacity: 1,
			})),
			transition('collapsed <=> expanded', [
				animate('300ms ease-in-out')
			])
		]),
		trigger('rotateArrow', [
			state('collapsed', style({
				transform: 'rotate(0deg)'
			})),
			state('expanded', style({
				transform: 'rotate(90deg)'
			})),
			transition('collapsed <=> expanded', [
				animate('300ms')
			])
		])
	],
})
export class ApplicationListFilterComponent implements OnInit, OnDestroy {
	@Output() filterChanged = new EventEmitter<ApplicationTableFilterForm>();

	accountList: TenantAccount[] = [];
	organizationList: OrganizationTree[] = [];
	categoryList: Category[] = [];
	tagList: CatalogTag[] = [];

	tenantId: string;

	form: FormGroup = new FormGroup({
		name: new FormControl(''),
		criticality: new FormControl([]),
		hostingType: new FormControl([]),
		status: new FormControl([]),

		tag: new FormControl([]),
		tagSearch: new FormControl(''),

		organization: new FormControl([]),
		organizationSearch: new FormControl(''),

		account: new FormControl([]),
		accountSearch: new FormControl(''),

		category: new FormControl([]),
		categorySearch: new FormControl(''),

		globalSearch: new FormControl('')
	});

	activeFilters: ApplicationFilter[] = [];

	list: {
		criticality: CriticalityLevel[],
		organization: OrganizationTree[],
		account: TenantAccount[],
		hostingType: HostingType[],
		category: Category[],
		tag: CatalogTag[],
		status: ApplicationStatus[]
	};

	filterSelected?: 'criticality' | 'organization' | 'account' | 'type' | 'category' | 'status' | 'tag';

	public globalSearchResults: GlobalSearchResult[] = [];

	openSearch: boolean = false;
	openFilters: boolean = false;

	private currentTenantService = inject(CurrentTenantService);
	private organizationService = inject(OrganizationService);
	private tenantAccountService = inject(TenantAccountService);
	private applicationService = inject(NewApplicationService);
	private applicationCategoryService = inject(ApplicationCategoryService);
	private translate = inject(TranslateService);

	_loading = false;
	private destroy$ = new Subject<void>();

	ngOnInit() {
		// React to tenant changes
		this.currentTenantService
			.getCurrentTenantChanges()
			.pipe(
				tap((tenant) => (this.tenantId = tenant.configuration.id)),
				switchMap(() => this.fetchFilterData()),
				takeUntil(this.destroy$)
			)
			.subscribe();

		// React to form changes
		merge(
			this.form.get('name')!.valueChanges.pipe(
				debounceTime(200),
				distinctUntilChanged()
			),
			this.form.get('criticality')!.valueChanges,
			this.form.get('hostingType')!.valueChanges,
			this.form.get('status')!.valueChanges,
			this.form.get('tag')!.valueChanges,
			this.form.get('organization')!.valueChanges,
			this.form.get('account')!.valueChanges,
			this.form.get('category')!.valueChanges
		)
			.pipe(
				debounceTime(100),
				tap(() => this.setActiveFilters()),
				map(() => this.buildFilterForm()),
				tap((form) => this.filterChanged.emit(form)),
				takeUntil(this.destroy$)
			)
			.subscribe();

		this.form.get('categorySearch')!.valueChanges
			.pipe(
				distinctUntilChanged(),
				tap((search) => this.searchCategoryFilter(search)),
				takeUntil(this.destroy$)
			)
			.subscribe();

		this.form.get('accountSearch')!.valueChanges
			.pipe(
				distinctUntilChanged(),
				tap((search) => this.searchAccountFilter(search)),
				takeUntil(this.destroy$)
			)
			.subscribe();

		this.form.get('organizationSearch')!.valueChanges
			.pipe(
				distinctUntilChanged(),
				tap((search) => this.searchOrganizationFilter(search)),
				takeUntil(this.destroy$)
			)
			.subscribe();

		this.form.get('tagSearch')!.valueChanges
			.pipe(
				distinctUntilChanged(),
				tap((search) => this.searchTagFilter(search)),
				takeUntil(this.destroy$)
			)
			.subscribe();

		this.form.get('globalSearch')!.valueChanges
			.pipe(
				distinctUntilChanged(),
				tap((search) => this.updateGlobalSearchResults(search)),
				takeUntil(this.destroy$)
			)
			.subscribe();
	}

	private fetchFilterData(): Observable<void> {
		return this.setLoading(true).pipe(
			switchMap(() =>
				forkJoin([
					this.organizationService.getOrganizationTreeByTenantId(
						this.tenantId
					),
					this.tenantAccountService.getAllTenantAccountByTenantId(
						this.tenantId
					),
					this.applicationCategoryService.getAllApplicationCategoryByTenantId(
						this.tenantId
					),
					this.applicationService.getAllApplicationTagByTenantId(
						this.tenantId
					)
				])
			),
			tap(([o, u, c, t]) => this.buildFilters(o, u, c, t)),
			map(() => void 0),
			finalize(() => this.setLoading(false))
		);
	}

	private buildFilters(
		organization: OrganizationTree,
		users: TenantAccount[],
		categories: Category[],
		tags: CatalogTag[]
	) {
		this.accountList = users;

		this.organizationList = organization.children
			.filter((c) => c.children.length > 0)
			.sort((a, b) =>
				a.organization.name.localeCompare(b.organization.name)
			);
		this.organizationList.forEach((o) =>
			o.children.sort((a, b) =>
				a.organization.name.localeCompare(b.organization.name)
			)
		);

		this.categoryList = categories.sort((a, b) =>
			a.name.localeCompare(b.name)
		);

		const sortedTags = tags.sort((a, b) => a.name.localeCompare(b.name));

		this.tagList = sortedTags;

		this.list = {
			criticality: Object.values(CriticalityLevel),
			organization: this.organizationList,
			account: this.accountList,
			hostingType: Object.values(HostingType),
			category: this.categoryList,
			tag: sortedTags,
			status: Object.values(ApplicationStatus)
		}
	}

	private buildFilterForm(): ApplicationTableFilterForm {
		return {
			name:
				this.form.get('name')!.value?.trim().length > 0
					? this.form.get('name')!.value
					: null,
			category: (
				(this.form.get('category')!.value as Category[]) ?? []
			).map((c) => c.categoryId),
			criticality:
				(this.form.get('criticality')!.value as CriticalityLevel[]) ??
				[],
			organization: (
				(this.form.get('organization')!.value as Organization[]) ?? []
			).map((o) => o.organizationId),
			responsible: (
				(this.form.get('account')!.value as TenantAccount[]) ?? []
			).map((r) => r.account.accountId),
			hostingType:
				(this.form.get('hostingType')!.value as HostingType[]) ?? [],
			status: (this.form.get('status')!.value as ApplicationStatus[]) ?? [],
			tag: ((this.form.get('tag')!.value as CatalogTag[]) ?? []).map(
				(t) => t.tagId
			)
		};
	}

	private setActiveFilters(): void {
		this.activeFilters = [
			...(this.form.get('criticality')!.value as CriticalityLevel[])
				.map(c => ({form: 'criticality', id: c, value: this.translate.instant('page.appDetails.businessCriticality.' + c.toLowerCase())})),
			...(this.form.get('organization')!.value as Organization[])
				.map(c => ({form: 'organization', id: c.organizationId, value: c.name})),
			...(this.form.get('account')!.value as TenantAccount[])
				.map(c => ({form: 'account', id: c.account.accountId, value: c.account.lastName + ' ' + c.account.firstName})),
			...(this.form.get('hostingType')!.value as HostingType[])
				.map(c => ({form: 'hostingType', id: c, value: this.translate.instant('page.dashboard.typology.' + c)})),
			...(this.form.get('category')!.value as Category[])
				.map(c => ({form: 'category', id: c.categoryId, value: c.name})),
			...(this.form.get('status')!.value as ApplicationStatus[])
				.map(c => ({form: 'status', id: c, value: this.translate.instant('page.appDetails.lifeCycle.' + c)})),
			...(this.form.get('tag')!.value as CatalogTag[])
				.map(c => ({form: 'tag', id: c.tagId, value: c.name})),
		];
		if (this.activeFilters.length <= 0) {
			this.openFilters = false;
		}
	}

	removeFilter(filter: ApplicationFilter) {
		switch (filter.form) {
			case 'category':
				const categoryList: Category[] = (this.form.get('category')!.value as Category[])
					.filter(c => c.categoryId !== filter.id)
				this.form.get('category')!.setValue(categoryList);
				break;
			case 'criticality':
				const criticalityList: CriticalityLevel[] = (this.form.get('criticality')!.value as CriticalityLevel[])
					.filter(c => c !== filter.id)
				this.form.get('criticality')!.setValue(criticalityList);
				break;
			case 'hostingType':
				const typeList: HostingType[] = (this.form.get('hostingType')!.value as HostingType[])
					.filter(c => c !== filter.id)
				this.form.get('hostingType')!.setValue(typeList);
				break;
			case 'status':
				const statusList: ApplicationStatus[] = (this.form.get('status')!.value as ApplicationStatus[])
					.filter(s => s !== filter.id)
				this.form.get('status')!.setValue(statusList);
				break;
			case 'tag':
				const tagList: CatalogTag[] = (this.form.get('tag')!.value as CatalogTag[])
					.filter(t => t.tagId !== filter.id)
				this.form.get('tag')!.setValue(tagList);
				break;
			case 'organization':
				const organizationList: Organization[] = (this.form.get('organization')!.value as Organization[])
					.filter(c => c.organizationId !== filter.id)
				this.form.get('organization')!.setValue(organizationList);
				break;
			case 'account':
				const responsibleList: TenantAccount[] = (this.form.get('account')!.value as TenantAccount[])
					.filter(c => c.account.accountId !== filter.id)
				this.form.get('account')!.setValue(responsibleList);
				break;
			default:
				break;
		}
	}

	onCheckboxChangeArray(formControlName: string, value: any, event: Event, isParent: boolean = false, isOrganization: boolean = false): void {
		if (isParent) {
			return this.onParentCheckChange(value, event)
		}

		if (isOrganization) {
			return this.onChildrenCheckChange(value, event);
		}

		const checked = (event.target as HTMLInputElement).checked;

		const currentArray = this.form.get(formControlName)!.value as any[];

		if (checked) {
			let valuesToAdd = [value];

			const newArray = [...currentArray];

			for (const val of valuesToAdd) {
				if (!this.containsObject(newArray, val, formControlName)) {
					newArray.push(val);
				}
			}

			this.form.get(formControlName)!.setValue(newArray);
		} else {
			let valuesToRemove = [value];

			const newArray = currentArray.filter(v =>
				!valuesToRemove.some(valToRemove => this.isSameObject(v, valToRemove, formControlName))
			);

			this.form.get(formControlName)!.setValue(newArray);
		}
	}


	private containsObject(array: any[], value: any, formControlName: string): boolean {
		return array.some(v => this.isSameObject(v, value, formControlName));
	}

	private isSameObject(a: any, b: any, formControlName: string): boolean {
		switch (formControlName) {
			case 'category':
				return a.categoryId === b.categoryId;
			case 'tag':
				return a.tagId === b.tagId;
			case 'organization':
				return a.organizationId === b.organizationId;
			case 'account':
				return a.account.accountId === b.account.accountId;
			default:
				return a === b;
		}
	}

	areAllChildrenChecked(parent: OrganizationTree): boolean {
		if (!parent.children || parent.children.length === 0) {
			return false;
		}

		return parent.children.every(child => {
			const childControl = this.form.get('organization')!.value as Organization[];
			return childControl.some(c => c.organizationId === child.organization.organizationId);
		});
	}

	onParentCheckChange(parent: OrganizationTree, event: Event): void {
		const checked = (event.target as HTMLInputElement).checked;
		const currentArray = this.form.get('organization')!.value as Organization[];

		const newArray = [...currentArray];
		if (checked) {
			parent.children.forEach(child => {
				if (!this.containsObject(newArray, child.organization, 'organization')) {
					newArray.push(child.organization);
				}
			});
		} else {
			parent.children.forEach(child => {
				const index = newArray.findIndex(c => c.organizationId === child.organization.organizationId);
				if (index !== -1) {
					newArray.splice(index, 1);
				}
			});
		}
		this.form.get('organization')!.setValue(newArray);
	}

	onChildrenCheckChange(child: any, event: Event): void {
		const checked = (event.target as HTMLInputElement).checked;
		const currentArray = this.form.get('organization')!.value as Organization[];

		const newArray = [...currentArray];

		if (checked) {
			if (!this.containsObject(newArray, child, 'organization')) {
				newArray.push(child.organization);
			}
		} else {
			const index = newArray.findIndex(c => c.organizationId === child.organization.organizationId);
			if (index !== -1) {
				newArray.splice(index, 1);
			}
		}

		this.form.get('organization')!.setValue(newArray);
	}

	isChildChecked(child: any): boolean {
		const selectedOrgs = this.form.get('organization')!.value as Organization[];
		return selectedOrgs.some(c => c.organizationId === child.organization.organizationId);
	}

	openSearchPanel(input: HTMLInputElement): void {
		this.openSearch = !this.openSearch;
		if (this.openSearch) {
			input.focus();
		} else {
			this.form.get('name')!.setValue('');
		}
	}

	clearFilters(): void {
		this.form.setValue({
			name: '',
			criticality: [],
			hostingType: [],
			status: [],
			tag: [],
			tagSearch: '',
			organization: [],
			organizationSearch: '',
			account: [],
			accountSearch: '',
			category: [],
			categorySearch: '',
			globalSearch: ''
		}, { emitEvent: false });

		this.form.get('globalSearch')!.setValue('');

		this.setActiveFilters();
		this.filterChanged.emit(this.buildFilterForm());
	}

	clearSpecificFilter(filter: 'criticality' | 'organization' | 'account' | 'type' | 'category' | 'status' | 'tag'): void {
		switch (filter) {
			case 'category':
				this.form.get('category')!.setValue([]);
				this.form.get('categorySearch')!.setValue('');
				break;
			case 'criticality':
				this.form.get('criticality')!.setValue([]);
				break;
			case 'type':
				this.form.get('hostingType')!.setValue([]);
				break;
			case 'status':
				this.form.get('status')!.setValue([]);
				break;
			case 'tag':
				this.form.get('tag')!.setValue([]);
				this.form.get('tagSearch')!.setValue('');
				break;
			case 'organization':
				this.form.get('organization')!.setValue([]);
				this.form.get('organizationSearch')!.setValue('');
				break;
			case 'account':
				this.form.get('account')!.setValue([]);
				this.form.get('accountSearch')!.setValue('');
				break;
		}
		this.filterSelected = undefined;
	}

	getCurrentFilterSearchInput(): FormControl {
		switch (this.filterSelected) {
			case 'category':
				return this.form.get('categorySearch') as FormControl;
			case 'account':
				return this.form.get('accountSearch') as FormControl;
			case 'tag':
				return this.form.get('tagSearch') as FormControl;
			default:
				return this.form.get('organizationSearch') as FormControl;
		}
	}

	private searchCategoryFilter(search?: string): void {
		this.list.category = this.categoryList.filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()));
	}

	private searchAccountFilter(search?: string): void {
		this.list.account = this.accountList.filter(c => !search || c.account.lastName.toLowerCase().includes(search.toLowerCase()) || c.account.firstName.toLowerCase().includes(search.toLowerCase()));
	}

	private searchOrganizationFilter(search?: string): void {
		if (!search) {
			this.list.organization = this.organizationList.map(it => {
				it.organization.expanded = false;
				return it;
			});
		} else {
			const lowercaseValue = search.toLowerCase();
			this.list.organization = this.organizationList
				.reduce((acc: OrganizationTree[], org) => {
					const matchingChildren = org.children.filter(team =>
						team.organization.name.toLowerCase().includes(lowercaseValue)
					);

					if (org.organization.name.toLowerCase().includes(lowercaseValue) || matchingChildren.length > 0) {
						acc.push({
							...org,
							children: org.organization.name.toLowerCase().includes(lowercaseValue)
								? org.children
								: matchingChildren
						});
					}
					return acc;
				}, [])
				.map(org => {
					org.organization.expanded = true
					return org;
				})
				.sort((a, b) => a.organization.name.localeCompare(b.organization.name));
		}
	}

	private searchTagFilter(search?: string): void {
		this.list.tag = this.tagList.filter(t => !search || t.name.toLowerCase().includes(search.toLowerCase()));
	}

	private updateGlobalSearchResults(search: string): void {
		if (!search) {
			this.globalSearchResults = [];
			return;
		}

		const lowercaseSearch = search.toLowerCase();

		const results: GlobalSearchResult[] = [];

		this.list.tag.forEach(tag => {
			if (tag.name.toLowerCase().includes(lowercaseSearch)) {
				results.push({
					type: 'tag',
					item: tag,
					breadcrumb: {
						top: this.translate.instant('page.application.table.filters.tags'),
						value: tag.name,
					},
					selected: this.form.get('tag')!.value.some((t: CatalogTag) => t.tagId === tag.tagId)
				});
			}
		});

		this.organizationList.forEach(org => {
			if (org.organization.name.toLowerCase().includes(lowercaseSearch)) {
				results.push({
					type: 'organization',
					item: org,
					breadcrumb: {
						top: this.translate.instant('page.application.table.filters.team'),
						value: org.organization.name,
					},
					selected: this.areAllChildrenChecked(org),
					isParent: true
				});

				org.children.forEach(child => {
					results.push({
						type: 'organization',
						item: child,
						breadcrumb: {
							top: this.translate.instant('page.application.table.filters.team'),
							middle: org.organization.name,
							value: child.organization.name,
						},
						selected: this.isChildChecked(child)
					});
				})
			} else {
				org.children.forEach(team => {
					if (team.organization.name.toLowerCase().includes(lowercaseSearch)) {
						results.push({
							type: 'organization',
							item: team,
							breadcrumb: {
								top: this.translate.instant('page.application.table.filters.team'),
								middle: org.organization.name,
								value: team.organization.name,
							},
							selected: this.isChildChecked(team)
						});
					}
				});
			}
		});

		this.accountList.forEach(account => {
			if (account.account.lastName.toLowerCase().includes(lowercaseSearch) || account.account.firstName.toLowerCase().includes(lowercaseSearch)) {
				results.push({
					type: 'account',
					item: account,
					breadcrumb: {
						top: this.translate.instant('page.application.table.filters.responsible'),
						value: `${account.account.lastName} ${account.account.firstName}`,
					},
					selected: this.form.get('account')!.value.some((a: TenantAccount) => a.account.accountId === account.account.accountId)
				});
			}
		});

		this.list.category.forEach(category => {
			if (category.name.toLowerCase().includes(lowercaseSearch)) {
				results.push({
					type: 'category',
					item: category,
					breadcrumb: {
						top: this.translate.instant('page.application.table.filters.category'),
						value: category.name,
					},
					selected: this.form.get('category')!.value.some((c: Category) => c.categoryId === category.categoryId)
				});
			}
		});

		this.list.criticality.forEach(criticality => {
			const criticalityName = this.translate.instant('page.appDetails.businessCriticality.' + criticality.toLowerCase());
			if (criticalityName.toLowerCase().includes(lowercaseSearch)) {
				results.push({
					type: 'criticality',
					item: criticality,
					breadcrumb: {
						top: this.translate.instant('page.application.table.filters.criticality'),
						value: criticalityName,
					},
					selected: this.form.get('criticality')!.value.includes(criticality)
				});
			}
		});

		this.list.hostingType.forEach(hostingType => {
			const hostingTypeName = this.translate.instant('page.dashboard.typology.' + hostingType);
			if (hostingTypeName.toLowerCase().includes(lowercaseSearch)) {
				results.push({
					type: 'hostingType',
					item: hostingType,
					breadcrumb: {
						top: this.translate.instant('page.application.table.filters.type'),
						value: hostingTypeName,
					},
					selected: this.form.get('hostingType')!.value.includes(hostingType)
				});
			}
		});

		this.list.status.forEach(status => {
			const statusName = this.translate.instant('page.appDetails.lifeCycle.' + status);
			if (statusName.toLowerCase().includes(lowercaseSearch)) {
				results.push({
					type: 'status',
					item: status,
					breadcrumb: {
						top: this.translate.instant('page.application.table.filters.status'),
						value: statusName,
					},
					selected: this.form.get('status')!.value.includes(status)
				});
			}
		});

		results.sort((a, b) => {
			if (a.type === b.type) {
				return a.item.name?.localeCompare(b.item.name) || 0;
			}
			return a.type.localeCompare(b.type);
		});

		if (results.length > 5) {
			results.length = 5;
		}

		this.globalSearchResults = results;
	}

	checkIfGlobalSearchResultSelected(result: GlobalSearchResult): boolean {
		switch (result.type) {
			case 'tag':
				return this.form.get('tag')!.value.some((t: CatalogTag) => t.tagId === result.item.tagId);
			case 'organization':
				if (result.isParent) {
					this.areAllChildrenChecked(result.item)
				}
				return this.form.get('organization')!.value.some((o: Organization) => o.organizationId === result.item.organization.organizationId);
			case 'account':
				return this.form.get('account')!.value.some((a: TenantAccount) => a.account.accountId === result.item.account.accountId);
			case 'category':
				return this.form.get('category')!.value.some((c: Category) => c.categoryId === result.item.categoryId);
			case 'criticality':
				return this.form.get('criticality')!.value.includes(result.item);
			case 'hostingType':
				return this.form.get('hostingType')!.value.includes(result.item);
			case 'status':
				return this.form.get('status')!.value.includes(result.item);
			default:
				return false;
		}
	}


	private setLoading(isLoading: boolean): Observable<void> {
		this._loading = isLoading;
		return of(void 0);
	}

	get searchValue(): string {
		return this.form.get('name')!.value;
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}

interface ApplicationFilter {
	form: string;
	id: string;
	value: string;
}

interface GlobalSearchResult {
	type: 'tag' | 'account' | 'organization' | 'criticality' | 'hostingType' | 'category' | 'status'
	item: any;
	breadcrumb: {
		top: string;
		middle?: string;
		value: string;
	};
	selected: boolean;
	isParent?: boolean;
}
